// src/shared/components/common/page-header.tsx

import type { LucideIcon } from 'lucide-react'
import type { PropsWithChildren, ReactNode } from 'react'

import { cn } from '@/shared/utils/cn'

interface PageHeaderProps extends PropsWithChildren {
  title: string
  subtitle?: string
  icon?: LucideIcon
  actions?: ReactNode
  back?: ReactNode
  breadcrumbs?: ReactNode
  tabs?: ReactNode
  className?: string
}

export function PageHeader({
  title,
  subtitle,
  icon: Icon,
  actions,
  back,
  breadcrumbs,
  tabs,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('mb-5 space-y-3', className)}>
      {breadcrumbs ? (
        <div className="text-xs text-[var(--color-text-muted)]">{breadcrumbs}</div>
      ) : null}
      {back ? <div>{back}</div> : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {Icon ? (
            <span className="mt-0.5 flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[var(--color-accent)]/12 text-[var(--color-accent)]">
              <Icon className="h-5 w-5" />
            </span>
          ) : null}
          <div className="min-w-0">
            <h1 className="font-display truncate text-2xl font-semibold tracking-tight text-[var(--color-text)] sm:text-3xl">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-1 truncate text-sm text-[var(--color-text-soft)]">{subtitle}</p>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {tabs ? <div className="-mb-1">{tabs}</div> : null}
    </header>
  )
}
